import React, { useState, useEffect, useRef, useMemo } from 'react';
import { get } from 'aws-amplify/api';
import TaskComparison from './TaskComparison';
import RealTimeMonitor from './RealTimeMonitor';
import './Dashboard.css';
import { getLocalDateString } from '../utils/date';

// ========== カラーパレット ==========
const TASK_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1',
  '#14B8A6', '#F43F5E', '#A855F7', '#22C55E', '#EAB308',
];

// ========== ズーム設定 ==========
const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8];
const ZOOM_DEFAULT_IDX = 2;

const LABEL_W = 150;
const ROW_H = 48;
const TIME_H = 36;
const MIN_BASE_W = 1000;
const MIN_PX_PER_DAY = 120;

// ========== ヘルパー関数 ==========
function fmtDateLong(d) {
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

function formatHM(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatDateShort(d) {
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

function formatDurationMs(ms) {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0 && m > 0) return `${h}時間${m}分`;
  if (h > 0) return `${h}時間`;
  if (m > 0) return `${m}分`;
  return `${totalSec}秒`;
}

function formatDurationSec(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h}時間${m}分`;
  if (h > 0) return `${h}時間`;
  if (m > 0) return `${m}分`;
  return `${Math.floor(seconds)}秒`;
}

function formatHours(hours) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h > 0 && m > 0) return `${h}時間${m}分`;
  if (h > 0) return `${h}時間`;
  if (m > 0) return `${m}分`;
  return '0分';
}

// ========== 期間計算 ==========
function getDateRange(period, customStart, customEnd) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (period) {
    case 'today': {
      const e = new Date(today.getTime() + 86400000);
      return { start: today, end: e, label: `今日（${fmtDateLong(today)}）` };
    }
    case 'yesterday': {
      const s = new Date(today); s.setDate(s.getDate() - 1);
      return { start: s, end: new Date(today), label: `昨日（${fmtDateLong(s)}）` };
    }
    case 'thisWeek': {
      const dow = today.getDay();
      const diff = dow === 0 ? -6 : 1 - dow;
      const s = new Date(today); s.setDate(today.getDate() + diff);
      const e = new Date(s); e.setDate(s.getDate() + 7);
      return { start: s, end: e, label: `今週（${fmtDateLong(s)} ～ ${fmtDateLong(new Date(e - 1))}）` };
    }
    case 'lastWeek': {
      const dow = today.getDay();
      const diff = dow === 0 ? -6 : 1 - dow;
      const thisMonday = new Date(today); thisMonday.setDate(today.getDate() + diff);
      const s = new Date(thisMonday); s.setDate(thisMonday.getDate() - 7);
      return { start: s, end: new Date(thisMonday), label: `先週（${fmtDateLong(s)} ～ ${fmtDateLong(new Date(thisMonday - 1))}）` };
    }
    case 'thisMonth': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { start: s, end: e, label: `今月（${now.getFullYear()}年${now.getMonth() + 1}月）` };
    }
    case 'lastMonth': {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: s, end: e, label: `先月（${s.getFullYear()}年${s.getMonth() + 1}月）` };
    }
    case 'custom': {
      if (customStart && customEnd) {
        const s = new Date(customStart);
        const e = new Date(customEnd); e.setDate(e.getDate() + 1);
        return { start: s, end: e, label: `${fmtDateLong(s)} ～ ${fmtDateLong(new Date(e - 1))}` };
      }
      const e = new Date(today.getTime() + 86400000);
      return { start: today, end: e, label: `今日（${fmtDateLong(today)}）` };
    }
    default: {
      const e = new Date(today.getTime() + 86400000);
      return { start: today, end: e, label: `今日（${fmtDateLong(today)}）` };
    }
  }
}

// ========== X軸ティック生成 ==========
function generateTicks(start, end) {
  const rangeDays = (end - start) / 86400000;
  const ticks = [];
  if (rangeDays <= 1) {
    const t = new Date(start); t.setMinutes(0, 0, 0);
    while (t <= end) { ticks.push(new Date(t)); t.setHours(t.getHours() + 2); }
  } else if (rangeDays <= 7) {
    const t = new Date(start); t.setHours(0, 0, 0, 0);
    while (t <= end) { ticks.push(new Date(t)); t.setDate(t.getDate() + 1); }
  } else if (rangeDays <= 31) {
    const t = new Date(start); t.setHours(0, 0, 0, 0);
    while (t < end) { ticks.push(new Date(t)); t.setDate(t.getDate() + 7); }
    ticks.push(new Date(end));
  } else {
    const t = new Date(start); t.setHours(0, 0, 0, 0);
    while (t < end) { ticks.push(new Date(t)); t.setDate(t.getDate() + 14); }
    ticks.push(new Date(end));
  }
  return ticks;
}

// 1時間ごとの補助目盛り（どの時間帯に作業したか視認しやすくする）
function generateHourTicks(start, end) {
  const ticks = [];
  const t = new Date(start);
  t.setMinutes(0, 0, 0);
  if (t < start) t.setHours(t.getHours() + 1);
  while (t <= end) { ticks.push(new Date(t)); t.setHours(t.getHours() + 1); }
  return ticks;
}

// 選択期間をカレンダー月単位のページに分割する（長いカスタム期間を月ごとに表示するため）
function getMonthPages(start, end) {
  const pages = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor < end) {
    const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    const pageStart = new Date(Math.max(cursor.getTime(), start.getTime()));
    const pageEnd = new Date(Math.min(nextMonth.getTime(), end.getTime()));
    pages.push({ start: pageStart, end: pageEnd, label: `${cursor.getFullYear()}年${cursor.getMonth() + 1}月` });
    cursor = nextMonth;
  }
  return pages.length > 0 ? pages : [{ start, end, label: '' }];
}

function formatTickLabel(d, rangeDays) {
  if (rangeDays <= 1) return `${String(d.getHours()).padStart(2, '0')}:00`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// ========== 個人ガントチャートコンポーネント ==========
function PersonalGantt({ logs, rangeStart, rangeEnd, colorMap, displayName }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [zoomIdx, setZoomIdx] = useState(ZOOM_DEFAULT_IDX);
  const [tooltip, setTooltip] = useState(null);
  const [pageIdx, setPageIdx] = useState(0);

  const zoom = ZOOM_STEPS[zoomIdx];

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (w > 0) setContainerWidth(w);
    });
    obs.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);
    return () => obs.disconnect();
  }, []);

  // 長期間（カスタム指定）は月単位のページに分割して表示する
  const monthPages = useMemo(() => getMonthPages(rangeStart, rangeEnd), [rangeStart, rangeEnd]);
  useEffect(() => { setPageIdx(0); }, [rangeStart, rangeEnd]);
  const hasPagination = monthPages.length > 1;
  const page = monthPages[Math.min(pageIdx, monthPages.length - 1)];
  const pageStart = page.start;
  const pageEnd = page.end;

  const rangeDays = (pageEnd - pageStart) / 86400000;
  const rangeMs = pageEnd - pageStart;

  const minBaseW = rangeDays <= 1
    ? MIN_BASE_W
    : Math.max(MIN_BASE_W, Math.ceil(rangeDays) * MIN_PX_PER_DAY);
  const BASE_CHART_W = Math.max(minBaseW, containerWidth - LABEL_W);
  const CHART_W = Math.round(BASE_CHART_W * zoom);

  const ticks = useMemo(() => generateTicks(pageStart, pageEnd), [pageStart, pageEnd]);
  const pxPerHour = CHART_W / (rangeMs / 3600000);
  // 1時間あたり十分な幅がある場合のみ、1時間ごとの補助目盛りを表示する
  const hourTicks = useMemo(
    () => (pxPerHour >= 8 ? generateHourTicks(pageStart, pageEnd) : []),
    [pageStart, pageEnd, pxPerHour]
  );
  const svgH = TIME_H + ROW_H + 8;
  const toX = (d) => ((new Date(d) - pageStart) / rangeMs) * CHART_W;

  // このページ（表示中の月）に含まれる作業ログのみを抽出
  const pageLogs = useMemo(
    () => logs.filter(l => new Date(l.end_at) > pageStart && new Date(l.start_at) < pageEnd),
    [logs, pageStart, pageEnd]
  );

  // 使用タスク一覧（凡例用）
  const usedTaskNames = [...new Set(pageLogs.map(l => l.task_name || 'その他'))];

  return (
    <div className="my-gantt" ref={containerRef}>
      {/* 月ページ送り + ズームコントロール */}
      <div className="tc-gantt-controls">
        {hasPagination ? (
          <div className="gantt-month-nav">
            <button
              className="gantt-month-nav-btn"
              onClick={() => setPageIdx(i => Math.max(0, i - 1))}
              disabled={pageIdx === 0}
            >
              ← 前の月
            </button>
            <span className="gantt-month-nav-label">{page.label}</span>
            <button
              className="gantt-month-nav-btn"
              onClick={() => setPageIdx(i => Math.min(monthPages.length - 1, i + 1))}
              disabled={pageIdx === monthPages.length - 1}
            >
              次の月 →
            </button>
          </div>
        ) : <span />}
        <div className="gantt-zoom-controls">
          <button className="gantt-zoom-btn"
            onClick={() => setZoomIdx(i => Math.max(0, i - 1))}
            disabled={zoomIdx === 0} title="縮小">−</button>
          <span className="gantt-zoom-label">×{zoom}</span>
          <button className="gantt-zoom-btn"
            onClick={() => setZoomIdx(i => Math.min(ZOOM_STEPS.length - 1, i + 1))}
            disabled={zoomIdx === ZOOM_STEPS.length - 1} title="拡大">+</button>
        </div>
      </div>

      {pageLogs.length === 0 ? (
        <div className="no-data">この期間に作業データがありません</div>
      ) : (
        <>
          {/* ガントボディ */}
          <div className="gantt-body">
            {/* 固定ラベル列 */}
            <div className="gantt-label-col" style={{ width: LABEL_W }}>
              <div className="gantt-label-spacer" style={{ height: TIME_H }} />
              <div className="gantt-label-cell" style={{ height: ROW_H, background: '#FAFAFA' }}>
                {(displayName || 'あなた').length > 16
                  ? (displayName || 'あなた').slice(0, 15) + '…'
                  : (displayName || 'あなた')}
              </div>
            </div>

            {/* 横スクロールチャート */}
            <div className="gantt-chart-scroll">
              <svg width={CHART_W} height={svgH} style={{ display: 'block' }}>
                {/* 行背景 */}
                <rect x={0} y={TIME_H} width={CHART_W} height={ROW_H} fill="#FAFAFA" />

                {/* 1時間ごとの補助目盛り（点線・控えめ） */}
                {hourTicks.map((t, i) => {
                  const x = toX(t);
                  return (
                    <line key={`h${i}`} x1={x} y1={TIME_H} x2={x} y2={svgH - 4}
                      stroke="#E5E7EB" strokeWidth={1} strokeDasharray="2 3" />
                  );
                })}

                {/* 主目盛り（日付/期間の区切り・実線） */}
                {ticks.map((t, i) => {
                  const x = toX(t);
                  const anchor = i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle';
                  return (
                    <g key={i}>
                      <line x1={x} y1={TIME_H} x2={x} y2={svgH - 4}
                        stroke="#CBD5E1" strokeWidth={1} />
                      <text x={x} y={TIME_H - 8} textAnchor={anchor}
                        fontSize={12} fill="#6B7280" fontFamily="-apple-system, sans-serif">
                        {formatTickLabel(t, rangeDays)}
                      </text>
                    </g>
                  );
                })}

                <line x1={0} y1={TIME_H + ROW_H} x2={CHART_W} y2={TIME_H + ROW_H}
                  stroke="#F3F4F6" strokeWidth={1} />

                {/* 作業バー */}
                {pageLogs.map((log, i) => {
                  const x1 = Math.max(0, toX(log.start_at));
                  const x2 = Math.min(CHART_W, toX(log.end_at));
                  const barW = Math.max(3, x2 - x1);
                  const y = TIME_H + 8;
                  const h = ROW_H - 16;
                  const taskName = log.task_name || 'その他';
                  const color = colorMap[taskName] || '#3B82F6';
                  const startD = new Date(log.start_at);
                  const endD = new Date(log.end_at);
                  return (
                    <rect key={i} x={x1} y={y} width={barW} height={h}
                      fill={color} rx={4}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => setTooltip({
                        x: e.clientX, y: e.clientY,
                        taskName,
                        date: formatDateShort(startD),
                        start: formatHM(startD),
                        end: formatHM(endD),
                        duration: formatDurationMs(endD - startD),
                      })}
                      onMouseMove={(e) => setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : prev)}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  );
                })}
              </svg>
            </div>
          </div>

          {/* ツールチップ */}
          {tooltip && (
            <div className="gantt-tooltip" style={{ left: tooltip.x + 14, top: tooltip.y - 10 }}>
              <div className="gantt-tooltip-task">{tooltip.taskName}</div>
              {rangeDays > 1 && (
                <div className="gantt-tooltip-row">
                  <span className="gantt-tooltip-label">日付</span>
                  <span className="gantt-tooltip-value">{tooltip.date}</span>
                </div>
              )}
              <div className="gantt-tooltip-row">
                <span className="gantt-tooltip-label">開始</span>
                <span className="gantt-tooltip-value">{tooltip.start}</span>
              </div>
              <div className="gantt-tooltip-row">
                <span className="gantt-tooltip-label">終了</span>
                <span className="gantt-tooltip-value">{tooltip.end}</span>
              </div>
              <div className="gantt-tooltip-row">
                <span className="gantt-tooltip-label">時間</span>
                <span className="gantt-tooltip-value">{tooltip.duration}</span>
              </div>
            </div>
          )}

          {/* 凡例 */}
          {usedTaskNames.length > 0 && (
            <div className="tc-legend">
              {usedTaskNames.map(name => (
                <div key={name} className="tc-legend-item">
                  <span className="tc-legend-dot" style={{ background: colorMap[name] || '#9CA3AF' }} />
                  <span className="tc-legend-label">{name}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ========== フォルダ配下の全タスクIDを再帰的に取得 ==========
function getDescendantTaskIds(folderId, allItems) {
  const result = [];
  const children = allItems.filter(t => t.parent_id === folderId);
  children.forEach(child => {
    if (child.item_type === 'task') {
      result.push(child.task_id);
    } else if (child.item_type === 'folder') {
      result.push(...getDescendantTaskIds(child.task_id, allItems));
    }
  });
  return result;
}

// ========== メインコンポーネント ==========
function Dashboard({ user }) {
  const [activeTab, setActiveTab] = useState('myinfo');
  const [workLogs, setWorkLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // 私の情報タブ用の期間選択
  const [period, setPeriod] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // フォルダフィルター（''=全タスク、folder_id=そのフォルダ配下のみ）
  const [selectedFolderId, setSelectedFolderId] = useState('');

  useEffect(() => {
    fetchWorkLogs();
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const restOperation = get({ apiName: 'worktimerApi', path: '/tasks' });
      const response = await restOperation.response;
      const data = await response.body.json();
      setTasks(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('❌ タスク取得エラー:', e);
    }
  };

  const fetchWorkLogs = async () => {
    setLoading(true);
    try {
      const restOperation = get({ apiName: 'worktimerApi', path: '/worklogs' });
      const response = await restOperation.response;
      const data = await response.body.json();
      setWorkLogs(data);
    } catch (e) {
      console.error('❌ 作業ログ取得エラー:', e);
    } finally {
      setLoading(false);
    }
  };

  // 今日/今週/今月の作業時間
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dow = today.getDay();
    const weekStart = new Date(today); weekStart.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const calcHours = (logs) => logs.reduce((sum, log) => {
      if (!log.end_at) return sum;
      return sum + (new Date(log.end_at) - new Date(log.start_at)) / 3600000;
    }, 0);

    return {
      today: calcHours(workLogs.filter(l => new Date(l.start_at) >= today)),
      week:  calcHours(workLogs.filter(l => new Date(l.start_at) >= weekStart)),
      month: calcHours(workLogs.filter(l => new Date(l.start_at) >= monthStart)),
    };
  }, [workLogs]);

  // 選択期間の範囲
  const { start: rangeStart, end: rangeEnd, label: periodLabel } = useMemo(
    () => getDateRange(period, customStart, customEnd),
    [period, customStart, customEnd]
  );

  // フォルダ選択時：配下の全タスクIDセットを計算
  const folderTaskIds = useMemo(() => {
    if (!selectedFolderId) return null;
    const ids = getDescendantTaskIds(selectedFolderId, tasks);
    return new Set(ids);
  }, [selectedFolderId, tasks]);

  // 期間内のログ（end_at があるもの）+ フォルダフィルター
  const filteredLogs = useMemo(() => {
    return workLogs.filter(log => {
      if (!log.end_at) return false;
      const s = new Date(log.start_at);
      if (s < rangeStart || s >= rangeEnd) return false;
      // フォルダフィルター適用
      if (folderTaskIds !== null) {
        return log.task_id && folderTaskIds.has(log.task_id);
      }
      return true;
    });
  }, [workLogs, rangeStart, rangeEnd, folderTaskIds]);

  // タスクカラーマップ（保存色を優先、未設定はパレットから割り当て）
  const colorMap = useMemo(() => {
    // タスク名→保存色のマップ
    const savedColors = {};
    tasks.forEach(t => { if (t.color) savedColors[t.task_name] = t.color; });

    const names = [...new Set(filteredLogs.map(l => l.task_name || 'その他'))];
    const map = {};
    // 保存色がないタスクのみパレットインデックスで管理（保存色のタスクはインデックスを消費しない）
    const uncoloredNames = names.filter(n => !savedColors[n]);
    names.forEach(name => {
      if (savedColors[name]) {
        map[name] = savedColors[name];
      } else {
        const idx = uncoloredNames.indexOf(name);
        map[name] = TASK_COLORS[idx % TASK_COLORS.length];
      }
    });
    return map;
  }, [filteredLogs, tasks]);

  // 期間サマリー
  const periodSummary = useMemo(() => {
    const totalSec = filteredLogs.reduce((sum, l) =>
      sum + (new Date(l.end_at) - new Date(l.start_at)) / 1000, 0);
    const daysWorked = new Set(filteredLogs.map(l => new Date(l.start_at).toDateString())).size;
    return { totalSec, taskCount: filteredLogs.length, daysWorked };
  }, [filteredLogs]);

  // タスク内訳
  const taskBreakdown = useMemo(() => {
    const map = {};
    filteredLogs.forEach(log => {
      const name = log.task_name || 'その他';
      if (!map[name]) map[name] = { totalSec: 0, count: 0 };
      map[name].totalSec += (new Date(log.end_at) - new Date(log.start_at)) / 1000;
      map[name].count++;
    });
    return Object.entries(map).sort((a, b) => b[1].totalSec - a[1].totalSec);
  }, [filteredLogs]);

  const displayName = user?.display_name || user?.username || user?.email?.split('@')[0] || 'あなた';
  const today = getLocalDateString();

  if (loading) {
    return <div className="dashboard"><div className="loading">読み込み中...</div></div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>📊 ダッシュボード</h1>
      </div>

      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'myinfo' ? 'active' : ''}`}
          onClick={() => setActiveTab('myinfo')}
        >
          👤 私の情報
        </button>
        {user?.role === 'admin' && (
          <button
            className={`tab-button ${activeTab === 'comparison' ? 'active' : ''}`}
            onClick={() => setActiveTab('comparison')}
          >
            👥 ユーザー比較
          </button>
        )}
        {user?.role === 'admin' && (
          <button
            className={`tab-button ${activeTab === 'realtime' ? 'active' : ''}`}
            onClick={() => setActiveTab('realtime')}
          >
            🟢 稼働状況
          </button>
        )}
      </div>

      {/* ===== 私の情報タブ ===== */}
      {activeTab === 'myinfo' && (
        <>
          {/* 今日/今週/今月の作業時間カード */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-icon">📅</div>
              <div className="card-label">今日の作業時間</div>
              <div className="card-value">{formatHours(stats.today)}</div>
            </div>
            <div className="summary-card">
              <div className="card-icon">📆</div>
              <div className="card-label">今週の作業時間</div>
              <div className="card-value">{formatHours(stats.week)}</div>
            </div>
            <div className="summary-card">
              <div className="card-icon">📊</div>
              <div className="card-label">今月の作業時間</div>
              <div className="card-value">{formatHours(stats.month)}</div>
            </div>
          </div>

          {/* フォルダフィルター */}
          {tasks.filter(t => t.item_type === 'folder').length > 0 && (
            <div className="my-folder-filter">
              <label className="my-folder-filter-label">📁 フォルダ絞り込み：</label>
              <div className="my-folder-filter-btns">
                <button
                  className={`my-folder-btn${selectedFolderId === '' ? ' active' : ''}`}
                  onClick={() => setSelectedFolderId('')}
                >
                  すべて
                </button>
                {tasks.filter(t => t.item_type === 'folder').map(f => (
                  <button
                    key={f.task_id}
                    className={`my-folder-btn${selectedFolderId === f.task_id ? ' active' : ''}`}
                    onClick={() => setSelectedFolderId(f.task_id)}
                  >
                    📁 {f.task_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 期間選択ボタン */}
          <div className="tc-period-bar my-period-bar">
            {[
              { key: 'today',     label: '今日' },
              { key: 'yesterday', label: '昨日' },
              { key: 'thisWeek',  label: '今週' },
              { key: 'lastWeek',  label: '先週' },
              { key: 'thisMonth', label: '今月' },
              { key: 'lastMonth', label: '先月' },
              { key: 'custom',    label: 'カスタム' },
            ].map(p => (
              <button key={p.key}
                className={`tc-period-btn${period === p.key ? ' active' : ''}`}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* カスタム日付入力 */}
          {period === 'custom' && (
            <div className="tc-custom-range my-custom-range">
              <label>
                開始日
                <input type="date" value={customStart}
                  onChange={e => setCustomStart(e.target.value)} max={today} />
              </label>
              <span className="tc-range-sep">〜</span>
              <label>
                終了日
                <input type="date" value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)} max={today} min={customStart} />
              </label>
            </div>
          )}

          {/* 期間ラベル（中央） */}
          <div className="tc-period-label-wrap">
            <div className="tc-period-label">{periodLabel}</div>
          </div>

          {/* ガントチャート */}
          <div className="my-gantt-wrapper">
            <PersonalGantt
              logs={filteredLogs}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              colorMap={colorMap}
              displayName={displayName}
            />
          </div>

          {/* 集計サマリー */}
          {filteredLogs.length > 0 && (
            <div className="my-summary-section">
              <h3>📋 集計サマリー</h3>
              <div className="my-summary-cards">
                <div className="my-stat-card">
                  <div className="my-stat-label">総作業時間</div>
                  <div className="my-stat-value">{formatDurationSec(periodSummary.totalSec)}</div>
                </div>
                <div className="my-stat-card">
                  <div className="my-stat-label">タスク実行数</div>
                  <div className="my-stat-value">{periodSummary.taskCount}件</div>
                </div>
                <div className="my-stat-card">
                  <div className="my-stat-label">稼働日数</div>
                  <div className="my-stat-value">{periodSummary.daysWorked}日</div>
                </div>
              </div>
            </div>
          )}

          {/* タスク内訳テーブル */}
          {taskBreakdown.length > 0 && (
            <div className="my-breakdown-section">
              <h3>📊 タスク内訳</h3>
              <div className="my-breakdown-scroll">
                <table className="my-breakdown-table">
                  <thead>
                    <tr>
                      <th>📋 タスク</th>
                      <th>⏱️ 合計時間</th>
                      <th>🔁 実行回数</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taskBreakdown.map(([taskName, data]) => (
                      <tr key={taskName}>
                        <td className="my-task-name-cell">
                          <span className="tc-legend-dot"
                            style={{ background: colorMap[taskName] || '#9CA3AF' }} />
                          {taskName}
                        </td>
                        <td className="my-task-time">{formatDurationSec(data.totalSec)}</td>
                        <td className="my-task-count">{data.count}回</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {filteredLogs.length === 0 && (
            <div className="no-data">選択された期間に作業データがありません</div>
          )}
        </>
      )}

      {activeTab === 'comparison' && user?.role === 'admin' && <TaskComparison />}
      {activeTab === 'realtime'  && user?.role === 'admin' && <RealTimeMonitor />}
    </div>
  );
}

export default Dashboard;
